import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { get, isNil } from 'lodash';
import styles from './index.module.css';
import { onKeyDown } from '../../../../utils/onKeyDown';
import { CheckBox } from '../../../../nonFeatureComponents/CheckBox';
import {
  DEFAULT_SKILL_PRACTICE_LEVEL_VALUE,
  LEARNING_PRIORITY,
  SKILL_OPERATION
} from '../../../../constants/competency';
import SkillModal from '../../../../feature/SkillModal';
import AssessSlider from '../../../../nonFeatureComponents/AssessSlider';
import {
  createPeopleSkills,
  removeSkillById,
  updateSkill
} from '../../../../api/domain/skill';

const SkillItem = ({ skill, hiddenTitle, onChange }) => {
  const [practiceLevel, setPracticeLevel] = useState(
    get(
      skill,
      'reflectedInformation.practiceLevel',
      DEFAULT_SKILL_PRACTICE_LEVEL_VALUE.DONT_EXIST
    )
  );
  const [infoOpen, setInfoOpen] = useState(false);
  const [isUpdateSkillLoading, setUpdateSkillLoading] = useState(false);
  const [isLearningPriority, setIsLearningPriority] = useState(
    get(skill, 'reflectedInformation.isLearningPriority', false)
  );

  useEffect(() => {
    if (!isNil(skill.reflectedInformation)) {
      if (!isNil(skill.reflectedInformation.practiceLevel)) {
        setPracticeLevel(skill.reflectedInformation.practiceLevel);
      } else {
        setPracticeLevel(DEFAULT_SKILL_PRACTICE_LEVEL_VALUE.EXIST_VALID);
      }
      if (!isNil(skill.reflectedInformation.isLearningPriority)) {
        setIsLearningPriority(skill.reflectedInformation.isLearningPriority);
      } else {
        setIsLearningPriority(false);
      }
    }
  }, [skill.reflectedInformation]);

  const onPracticeLevelChange = async sliderPracticeLevel => {
    const newSkillReflectedInformation = {
      ...skill.reflectedInformation,
      practiceLevel:
        sliderPracticeLevel === practiceLevel
          ? DEFAULT_SKILL_PRACTICE_LEVEL_VALUE.DONT_EXIST
          : sliderPracticeLevel,
      isLearningPriority
    };
    await handleSkillChange(newSkillReflectedInformation);
  };

  const onIsLearningPriorityChange = async () => {
    const newSkillReflectedInformation = {
      ...skill.reflectedInformation,
      isLearningPriority: !isLearningPriority,
      practiceLevel
    };
    await handleSkillChange(newSkillReflectedInformation);
  };

  const handleSkillChange = async newSkill => {
    const tmpPracticeLevel =
      newSkill.practiceLevel ||
      (newSkill.isLearningPriority === false
        ? DEFAULT_SKILL_PRACTICE_LEVEL_VALUE.DONT_EXIST
        : DEFAULT_SKILL_PRACTICE_LEVEL_VALUE.EXIST_VALID);
    const tmpIsLearningPriority = newSkill.isLearningPriority || false;
    setIsLearningPriority(tmpIsLearningPriority);
    setPracticeLevel(tmpPracticeLevel);
    if (!isUpdateSkillLoading) {
      setUpdateSkillLoading(true);
    }
    const handleType = isNil(skill.reflectedInformation)
      ? SKILL_OPERATION.ADD_SKILL
      : tmpPracticeLevel === DEFAULT_SKILL_PRACTICE_LEVEL_VALUE.DONT_EXIST
      ? SKILL_OPERATION.REMOVE_SKILL
      : SKILL_OPERATION.UPDATE_SKILL;
    try {
      if (handleType === SKILL_OPERATION.ADD_SKILL) {
        const changedSkill = {
          practiceLevel: tmpPracticeLevel,
          isLearningPriority: tmpIsLearningPriority
        };
        const id = await createPeopleSkills(skill.skillId, changedSkill);
        onChange({
          skillId: skill.skillId,
          reflectedInformation: {
            practiceLevel: tmpPracticeLevel,
            isLearningPriority: tmpIsLearningPriority,
            id: id,
            skillId: skill.skillId
          }
        });
      } else if (handleType === SKILL_OPERATION.REMOVE_SKILL) {
        await removeSkillById(skill.reflectedInformation.id);
        onChange({
          skillId: skill.skillId,
          reflectedInformation: null
        });
      } else {
        await updateSkill(skill.reflectedInformation.id, {
          practiceLevel: tmpPracticeLevel,
          isLearningPriority: tmpIsLearningPriority
        });
        onChange({
          skillId: skill.skillId,
          reflectedInformation: {
            practiceLevel: tmpPracticeLevel,
            isLearningPriority: tmpIsLearningPriority
          }
        });
      }
      setUpdateSkillLoading(false);
    } catch (error) {
      resetSkill();
    }
  };

  const resetSkill = () => {
    setPracticeLevel(
      get(
        skill,
        'reflectedInformation.practiceLevel',
        DEFAULT_SKILL_PRACTICE_LEVEL_VALUE.DONT_EXIST
      )
    );
    setIsLearningPriority(
      get(skill, 'reflectedInformation.isLearningPriority', false)
    );
  };

  return (
    <div
      className={styles.competency}
      data-cy={`competency:${skill.identifier}`}
    >
      {!hiddenTitle && (
        <div
          className={clsx(styles.name, styles.firstColumn)}
          data-cy="competencyTitle"
        >
          <div
            className={styles.detailBtn}
            role="button"
            onKeyDown={e => onKeyDown(e, () => setInfoOpen(true))}
            tabIndex={0}
          >
            <div
              className={styles.detailBtnInner}
              tabIndex={-1}
              onClick={() => setInfoOpen(true)}
            >
              {skill.name}
            </div>
          </div>
        </div>
      )}
      <div className={clsx(styles.slider, styles.secondColumn)}>
        <AssessSlider
          suggestedLevel={skill.suggestedLevel}
          actualPracticeLevel={practiceLevel}
          levelDescriptions={skill.levelDescriptions?.sort((a, b) =>
            a.level > b.level ? 1 : -1
          )}
          name={skill.name}
          onChange={onPracticeLevelChange}
        />
      </div>
      <div className={clsx(styles.checkBox, styles.thirdColumn)}>
        <CheckBox
          label={LEARNING_PRIORITY}
          onChange={onIsLearningPriorityChange}
          checked={isLearningPriority}
          style={{ marginLeft: '3px' }}
        />
      </div>
      <SkillModal
        notShowSelectButton={true}
        skill={skill}
        open={infoOpen}
        close={() => setInfoOpen(false)}
      />
    </div>
  );
};

SkillItem.propTypes = {
  skill: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  hiddenTitle: PropTypes.bool
};

export default SkillItem;
